// @ts-check

import { AppRuntime } from '../app/app_runtime.js?v=20260706d';
import { S } from '../app/app_state.js?v=20260817c';
import {
  collectDimensionSnapCandidates,
  distance,
  distancePointToSegment,
  getDimensionGeometryRevision,
  normalizeGeometryRef
} from './dimension_service.js?v=20260904f';
import { getProjectModelRevision } from '../project/project_revision_service.js';
import {
  areaMeasurementBounds,
  areaMeasurementSourceKeys,
  createAreaMeasurementFromVertices,
  findAreaMeasurementContainingPoint,
  invalidateAreaMeasurementDerivedCachesForGeometryChange,
  normalizeAreaMeasurementEntity,
  pointInPolygon,
  polygonInteriorLabelPoint,
  polygonHasSelfIntersections,
  polygonSignedAreaSquareMillimeters,
  resolveAreaMeasurementPoints
} from './area_measurement_service.js?v=20260903c';

export const AUTO_AREA_MEASUREMENT_TOOL = 'auto_area_measurement';
export const AUTO_AREA_MEASUREMENT_DETECTION_VERSION = 2;

const MIN_SEGMENT_LENGTH_MM = 1;
const JOIN_TOLERANCE_MM = 2;
const MIN_BOUNDARY_EXTENSION_LENGTH_MM = 80;
const MAX_BOUNDARY_EXTENSION_MM = 250;
const DETAIL_EDGE_MAX_LENGTH_MM = 80;
const DOMINANT_EDGE_MIN_LENGTH_MM = 320;
const DETAIL_CLUSTER_RADIUS_MM = 160;
const DETAIL_CLUSTER_MIN_EDGES = 3;
const PARAMETER_TOLERANCE = 1e-7;
const MIN_FACE_AREA_SQUARE_MM = 1;
const MAX_CANDIDATE_SEGMENTS = 1600;

let lastAutomaticAreaProject = null;
let lastAutomaticAreaGeometrySignature = '';
let automaticAreaGeometryState = null;
let automaticAreaClickGeometryState = null;
let automaticAreaClickPreparationHandle = null;
let automaticAreaClickRequestRevision = 0;

const AUTOMATIC_AREA_SPATIAL_CELL_MM = 2000;
const AUTOMATIC_AREA_QUERY_MIN_RADIUS_MM = 1500;
const AUTOMATIC_AREA_QUERY_ATTEMPTS = 4;

function numeric(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function point(value = {}) {
  return { x: numeric(value.x), z: numeric(value.z ?? value.y) };
}

function sub(a, b) {
  return { x: numeric(a?.x) - numeric(b?.x), z: numeric(a?.z) - numeric(b?.z) };
}

function add(a, b) {
  return { x: numeric(a?.x) + numeric(b?.x), z: numeric(a?.z) + numeric(b?.z) };
}

function mul(value, scalar) {
  return { x: numeric(value?.x) * scalar, z: numeric(value?.z) * scalar };
}

function cross(a, b) {
  return numeric(a?.x) * numeric(b?.z) - numeric(a?.z) * numeric(b?.x);
}

function dot(a, b) {
  return numeric(a?.x) * numeric(b?.x) + numeric(a?.z) * numeric(b?.z);
}

function clamp01(value) {
  return Math.max(0, Math.min(1, numeric(value)));
}

function pointAt(segment, parameter) {
  return add(segment.a, mul(sub(segment.b, segment.a), numeric(parameter)));
}

function parameterOnSegment(candidate, segment) {
  const axis = sub(segment.b, segment.a);
  const denominator = dot(axis, axis);
  if (denominator <= Number.EPSILON) return 0;
  return dot(sub(candidate, segment.a), axis) / denominator;
}

function segmentGeometryKey(a, b, precision = 0.1) {
  const keyPoint = value => `${Math.round(value.x / precision)},${Math.round(value.z / precision)}`;
  const first = keyPoint(a);
  const second = keyPoint(b);
  return first < second ? `${first}|${second}` : `${second}|${first}`;
}

function refKey(ref) {
  const normalized = normalizeGeometryRef(ref);
  if (!normalized) return '';
  const copy = { ...normalized };
  delete copy.t;
  return JSON.stringify(copy);
}

function segmentScopeKey(segment) {
  const refs = segment?.sources?.map(source => normalizeGeometryRef(source?.ref)).filter(Boolean) || [];
  const ref = refs[0] || normalizeGeometryRef(segment?.ref);
  return [
    ref?.systemType || ref?.systemId || '',
    ref?.sourceId || ref?.groupId || ref?.entityId || segment?.id || ''
  ].join('|');
}

function isSketchWallBoundarySegment(segment) {
  const refs = segment?.sources?.map(source => normalizeGeometryRef(source?.ref)).filter(Boolean) || [];
  const fallbackRef = normalizeGeometryRef(segment?.ref);
  if (fallbackRef) refs.push(fallbackRef);
  return refs.some(ref => String(ref?.systemType || ref?.systemId || '') === 'sketchWalls');
}

function sourceFromCandidate(candidate, index) {
  const a = point(candidate?.a);
  const b = point(candidate?.b);
  if (distance(a, b) < MIN_SEGMENT_LENGTH_MM) return null;
  const ref = normalizeGeometryRef(candidate?.ref);
  return {
    id: `auto-edge-${index}`,
    a,
    b,
    ref: ref?.type === 'segment' ? ref : null,
    geometrySource: String(candidate?.geometrySource || ''),
    sources: [{ ref: ref?.type === 'segment' ? ref : null, a, b }],
    parameters: [0, 1]
  };
}

function segmentMidpoint(segment) {
  return pointAt(segment, 0.5);
}

function segmentsAreLocallyClose(first, second, radius) {
  const firstMidpoint = segmentMidpoint(first);
  const secondMidpoint = segmentMidpoint(second);
  return distance(firstMidpoint, secondMidpoint) <= radius
    || distancePointToSegment(firstMidpoint, second.a, second.b) <= radius
    || distancePointToSegment(secondMidpoint, first.a, first.b) <= radius;
}

// Automatische Flächen sollen von den langen, raumbildenden Konturen geführt
// werden. Winkel, U-Profile, Füsse und ähnliche Details erscheinen dagegen als
// dichte Bündel kurzer Kanten direkt neben einer langen Kante. Die Erkennung ist
// bewusst rein geometrisch: Ein einzelnes kurzes Wandstück bleibt erhalten;
// nur lokale Kurz-Kanten-Cluster neben einer dominanten Kante werden verworfen.
function suppressLocalBoundaryDetails(segments) {
  const dominant = segments.filter(segment => (
    distance(segment.a, segment.b) >= DOMINANT_EDGE_MIN_LENGTH_MM
  ));
  if (!dominant.length) return segments;

  const short = segments.filter(segment => (
    distance(segment.a, segment.b) <= DETAIL_EDGE_MAX_LENGTH_MM
  ));
  if (short.length < DETAIL_CLUSTER_MIN_EDGES) return segments;

  const shortCellsByScope = new Map();
  const cellCoordinate = value => Math.floor(value / DETAIL_CLUSTER_RADIUS_MM);
  for (const segment of short) {
    const scope = segmentScopeKey(segment);
    const midpoint = segmentMidpoint(segment);
    const key = spatialCellKey(cellCoordinate(midpoint.x), cellCoordinate(midpoint.z));
    const scopeCells = shortCellsByScope.get(scope) || new Map();
    const entries = scopeCells.get(key) || [];
    entries.push(segment);
    scopeCells.set(key, entries);
    shortCellsByScope.set(scope, scopeCells);
  }

  const nearbyShortSegments = segment => {
    const scopeCells = shortCellsByScope.get(segmentScopeKey(segment));
    if (!scopeCells) return [];
    const midpoint = segmentMidpoint(segment);
    const cellX = cellCoordinate(midpoint.x);
    const cellZ = cellCoordinate(midpoint.z);
    const nearby = [];
    for (let x = cellX - 1; x <= cellX + 1; x += 1) {
      for (let z = cellZ - 1; z <= cellZ + 1; z += 1) {
        nearby.push(...(scopeCells.get(spatialCellKey(x, z)) || []));
      }
    }
    return nearby;
  };

  const preferred = segments.filter(segment => {
    if (distance(segment.a, segment.b) > DETAIL_EDGE_MAX_LENGTH_MM) return true;
    let localShortEdgeCount = 0;
    for (const other of nearbyShortSegments(segment)) {
      if (!segmentsAreLocallyClose(segment, other, DETAIL_CLUSTER_RADIUS_MM)) continue;
      localShortEdgeCount += 1;
      if (localShortEdgeCount >= DETAIL_CLUSTER_MIN_EDGES) break;
    }
    if (localShortEdgeCount < DETAIL_CLUSTER_MIN_EDGES) return true;
    return !dominant.some(other => (
      segmentsAreLocallyClose(segment, other, DETAIL_CLUSTER_RADIUS_MM)
    ));
  });

  return preferred.length >= 3 ? preferred : segments;
}

function segmentsAreParallel(first, second) {
  const firstAxis = sub(first.b, first.a);
  const secondAxis = sub(second.b, second.a);
  const firstLength = distance(first.a, first.b);
  const secondLength = distance(second.a, second.b);
  if (firstLength < MIN_SEGMENT_LENGTH_MM || secondLength < MIN_SEGMENT_LENGTH_MM) return false;
  return Math.abs(cross(firstAxis, secondAxis)) <= firstLength * secondLength * 0.01;
}

function intervalCoverage(intervals = []) {
  if (!intervals.length) return 0;
  const sorted = intervals
    .map(([start, end]) => [Math.max(0, Math.min(start, end)), Math.min(1, Math.max(start, end))])
    .filter(([start, end]) => end > start)
    .sort((left, right) => left[0] - right[0]);
  let coverage = 0;
  let currentStart = null;
  let currentEnd = null;
  for (const [start, end] of sorted) {
    if (currentStart === null) {
      currentStart = start;
      currentEnd = end;
    } else if (start <= currentEnd + PARAMETER_TOLERANCE) {
      currentEnd = Math.max(currentEnd, end);
    } else {
      coverage += currentEnd - currentStart;
      currentStart = start;
      currentEnd = end;
    }
  }
  return coverage + (currentStart === null ? 0 : currentEnd - currentStart);
}

function suppressBoundsCoveredByPreciseEdges(segments) {
  const preciseByScope = new Map();
  const boundsByScope = new Map();
  for (const segment of segments) {
    const scope = segmentScopeKey(segment);
    const target = segment.geometrySource === 'precise'
      ? preciseByScope
      : segment.geometrySource === 'bounds'
        ? boundsByScope
        : null;
    if (!target) continue;
    const entries = target.get(scope) || [];
    entries.push(segment);
    target.set(scope, entries);
  }
  if (!preciseByScope.size) return segments;
  const coveredScopes = new Set();
  for (const [scope, boundsSegments] of boundsByScope.entries()) {
    const maxLength = Math.max(0, ...boundsSegments.map(segment => distance(segment.a, segment.b)));
    const dominantBounds = boundsSegments.filter(segment => distance(segment.a, segment.b) >= maxLength * 0.75);
    if (!dominantBounds.length) continue;
    const fullyCovered = dominantBounds.every(segment => {
      const intervals = (preciseByScope.get(scope) || [])
      .filter(other => (
        segmentsAreParallel(segment, other)
        && distancePointToSegment(other.a, segment.a, segment.b) <= DETAIL_CLUSTER_RADIUS_MM
        && distancePointToSegment(other.b, segment.a, segment.b) <= DETAIL_CLUSTER_RADIUS_MM
      ))
      .map(other => [parameterOnSegment(other.a, segment), parameterOnSegment(other.b, segment)]);
      return intervalCoverage(intervals) >= 0.9;
    });
    if (fullyCovered) coveredScopes.add(scope);
  }
  const preferred = segments.filter(segment => !(
    segment.geometrySource === 'bounds'
    && coveredScopes.has(segmentScopeKey(segment))
  ));
  return preferred.length >= 3 ? preferred : segments;
}

// Ein SaniGuard-Wandobjekt liefert sowohl seine durchgehende, raumbildende
// Kontur als auch die Konturen der einzelnen Platten, Tueren, Profile und
// Beschlaege. Die fruehere Optimierung entfernte die durchgehende Kontur,
// sobald irgendeine praezise Teilkante existierte. Bei Tueren blieben dadurch
// Luecken im Raumabschluss. Rein geometrisch gilt stattdessen: Eine deutlich
// laengere Kante desselben Objekts darf nahe, parallele Teilkanten ersetzen.
// Parametrische Waende sind davon ausgenommen: Ihre kuerzere Innenkante und
// laengere Aussenkante sind zwei vollwertige Wandseiten. Gerade bei kurzen,
// dicken oder spitz angeschlossenen Waenden kann die Innenkante weniger als
// 75 % der Aussenkante messen; sie zu entfernen oeffnet die Boden-Kontur und
// laesst die Automatik faelschlich bis zur Aussenkante laufen.
function suppressSubsegmentsCoveredByDominantEdges(segments) {
  const byScope = new Map();
  for (const segment of segments) {
    const scope = segmentScopeKey(segment);
    const entries = byScope.get(scope) || [];
    entries.push(segment);
    byScope.set(scope, entries);
  }
  const dominantByScope = new Map([...byScope.entries()].map(([scope, entries]) => [
    scope,
    entries.filter(other => distance(other.a, other.b) >= DOMINANT_EDGE_MIN_LENGTH_MM)
  ]));
  const preferred = segments.filter(segment => {
    if (isSketchWallBoundarySegment(segment)) return true;
    const segmentLength = distance(segment.a, segment.b);
    const dominant = dominantByScope.get(segmentScopeKey(segment)) || [];
    return !dominant.some(other => {
      if (other === segment) return false;
      const otherLength = distance(other.a, other.b);
      if (segmentLength > otherLength * 0.75
        || !segmentsAreParallel(segment, other)) return false;
      return distancePointToSegment(segment.a, other.a, other.b) <= DETAIL_CLUSTER_RADIUS_MM
        && distancePointToSegment(segment.b, other.a, other.b) <= DETAIL_CLUSTER_RADIUS_MM;
    });
  });
  return preferred.length >= 3 ? preferred : segments;
}

function prepareSegments(rawSegments = [], clickPoint = null) {
  const unique = new Map();
  rawSegments.forEach((candidate, index) => {
    if (candidate?.kind && candidate.kind !== 'segment') return;
    const segment = sourceFromCandidate(candidate, index);
    if (!segment) return;
    const key = segmentGeometryKey(segment.a, segment.b);
    const existing = unique.get(key);
    if (!existing) {
      unique.set(key, segment);
      return;
    }
    const sourceKey = refKey(segment.ref);
    if (!existing.sources.some(source => refKey(source.ref) === sourceKey)) {
      existing.sources.push(...segment.sources);
    }
    if (segment.geometrySource === 'precise') existing.geometrySource = 'precise';
  });
  const segments = suppressSubsegmentsCoveredByDominantEdges(
    suppressLocalBoundaryDetails(
      suppressBoundsCoveredByPreciseEdges([...unique.values()])
    )
  );
  if (segments.length <= MAX_CANDIDATE_SEGMENTS || !clickPoint) return segments;
  return segments
    .sort((left, right) => (
      distancePointToSegment(clickPoint, left.a, left.b)
      - distancePointToSegment(clickPoint, right.a, right.b)
    ))
    .slice(0, MAX_CANDIDATE_SEGMENTS);
}

function addParameter(segment, value) {
  if (!Number.isFinite(value) || value < -PARAMETER_TOLERANCE || value > 1 + PARAMETER_TOLERANCE) return;
  const normalized = clamp01(value);
  if (!segment.parameters.some(current => Math.abs(current - normalized) <= PARAMETER_TOLERANCE)) {
    segment.parameters.push(normalized);
  }
}

function addExtendedParameter(segment, value) {
  if (!Number.isFinite(value)) return;
  if (!segment.parameters.some(current => Math.abs(current - value) <= PARAMETER_TOLERANCE)) {
    segment.parameters.push(value);
  }
}

function lineIntersectionParameters(first, second) {
  const firstAxis = sub(first.b, first.a);
  const secondAxis = sub(second.b, second.a);
  const denominator = cross(firstAxis, secondAxis);
  const scale = Math.max(1, distance(first.a, first.b) * distance(second.a, second.b));
  if (Math.abs(denominator) <= scale * 1e-10) return null;
  const offset = sub(second.a, first.a);
  return {
    first: cross(offset, secondAxis) / denominator,
    second: cross(offset, firstAxis) / denominator
  };
}

function endpointSideForOutsideParameter(parameter) {
  if (parameter < -PARAMETER_TOLERANCE) return 0;
  if (parameter > 1 + PARAMETER_TOLERANCE) return 1;
  return null;
}

function endpointKey(segmentIndex, side) {
  return `${segmentIndex}:${side}`;
}

function endpointContinuesWithinScope(segments, segmentIndex, side) {
  const segment = segments[segmentIndex];
  const endpoint = side === 0 ? segment.a : segment.b;
  const axis = sub(segment.b, segment.a);
  const axisLength = distance(segment.a, segment.b);
  if (axisLength < MIN_SEGMENT_LENGTH_MM) return false;
  const outward = side === 0 ? mul(axis, -1 / axisLength) : mul(axis, 1 / axisLength);
  const scope = segmentScopeKey(segment);
  return segments.some((other, otherIndex) => {
    if (otherIndex === segmentIndex
      || segmentScopeKey(other) !== scope
      || distancePointToSegment(endpoint, other.a, other.b) > JOIN_TOLERANCE_MM) return false;
    return [other.a, other.b].some(otherPoint => {
      const offset = sub(otherPoint, endpoint);
      const outwardDistance = dot(offset, outward);
      const lineDistance = Math.abs(cross(axis, offset)) / axisLength;
      return outwardDistance > JOIN_TOLERANCE_MM && lineDistance <= JOIN_TOLERANCE_MM;
    });
  });
}

function endpointAlreadyMeetsForeignBoundary(segments, segmentIndex, side) {
  const segment = segments[segmentIndex];
  const endpoint = side === 0 ? segment.a : segment.b;
  const scope = segmentScopeKey(segment);
  return endpointContinuesWithinScope(segments, segmentIndex, side) || segments.some((other, otherIndex) => (
    otherIndex !== segmentIndex
    && segmentScopeKey(other) !== scope
    && distancePointToSegment(endpoint, other.a, other.b) <= JOIN_TOLERANCE_MM
  ));
}

// Wandpolygone enden an ihrer eigenen Stirnkante. Eine daran gesnappte Wand
// trifft die gewünschte Raumseite deshalb oft erst auf der gedachten
// Verlängerung um eine halbe Wandstärke. Pro freiem Ende wird nur der nächste
// plausible Schnitt mit einem ANDEREN Wandobjekt ergänzt. Müssen beide Linien
// verlängert werden, wird der Schnitt nur bei gegenseitiger Zuordnung benutzt.
function extendBoundarySegmentsToNearbyIntersections(segments) {
  const proposals = new Map();
  segments.forEach((segment, segmentIndex) => {
    const segmentLength = distance(segment.a, segment.b);
    if (segmentLength < MIN_BOUNDARY_EXTENSION_LENGTH_MM) return;
    const scope = segmentScopeKey(segment);
    [0, 1].forEach(side => {
      if (endpointAlreadyMeetsForeignBoundary(segments, segmentIndex, side)) return;
      const endpoint = side === 0 ? segment.a : segment.b;
      let best = null;
      segments.forEach((other, otherIndex) => {
        if (otherIndex === segmentIndex
          || segmentScopeKey(other) === scope
          || distance(other.a, other.b) < MIN_BOUNDARY_EXTENSION_LENGTH_MM) return;
        const parameters = lineIntersectionParameters(segment, other);
        if (!parameters) return;
        const currentOutsideSide = endpointSideForOutsideParameter(parameters.first);
        if (currentOutsideSide !== side) return;
        const intersection = pointAt(segment, parameters.first);
        const ownExtension = distance(endpoint, intersection);
        const otherExtension = distancePointToSegment(intersection, other.a, other.b);
        if (ownExtension > MAX_BOUNDARY_EXTENSION_MM
          || otherExtension > MAX_BOUNDARY_EXTENSION_MM) return;
        const otherOutsideSide = endpointSideForOutsideParameter(parameters.second);
        const score = ownExtension + otherExtension * 1.25;
        if (!best || score < best.score) {
          best = {
            segmentIndex,
            side,
            otherIndex,
            otherOutsideSide,
            firstParameter: parameters.first,
            secondParameter: parameters.second,
            intersection,
            score
          };
        }
      });
      if (best) proposals.set(endpointKey(segmentIndex, side), best);
    });
  });

  proposals.forEach(proposal => {
    if (proposal.otherOutsideSide !== null) {
      const reciprocal = proposals.get(endpointKey(proposal.otherIndex, proposal.otherOutsideSide));
      if (!reciprocal
        || reciprocal.otherIndex !== proposal.segmentIndex
        || distance(reciprocal.intersection, proposal.intersection) > JOIN_TOLERANCE_MM) return;
    }
    addExtendedParameter(segments[proposal.segmentIndex], proposal.firstParameter);
    addExtendedParameter(segments[proposal.otherIndex], proposal.secondParameter);
  });
}

function splitAtPairIntersections(first, second) {
  const firstAxis = sub(first.b, first.a);
  const secondAxis = sub(second.b, second.a);
  const offset = sub(second.a, first.a);
  const denominator = cross(firstAxis, secondAxis);
  const scale = Math.max(1, distance(first.a, first.b) * distance(second.a, second.b));
  if (Math.abs(denominator) > scale * 1e-10) {
    const firstParameter = cross(offset, secondAxis) / denominator;
    const secondParameter = cross(offset, firstAxis) / denominator;
    if (firstParameter >= -PARAMETER_TOLERANCE && firstParameter <= 1 + PARAMETER_TOLERANCE
      && secondParameter >= -PARAMETER_TOLERANCE && secondParameter <= 1 + PARAMETER_TOLERANCE) {
      addParameter(first, firstParameter);
      addParameter(second, secondParameter);
    }
    return;
  }
  if (Math.abs(cross(offset, firstAxis)) > Math.max(1, distance(first.a, first.b)) * JOIN_TOLERANCE_MM) return;
  [second.a, second.b].forEach(candidate => {
    const parameter = parameterOnSegment(candidate, first);
    if (parameter >= -PARAMETER_TOLERANCE && parameter <= 1 + PARAMETER_TOLERANCE) addParameter(first, parameter);
  });
  [first.a, first.b].forEach(candidate => {
    const parameter = parameterOnSegment(candidate, second);
    if (parameter >= -PARAMETER_TOLERANCE && parameter <= 1 + PARAMETER_TOLERANCE) addParameter(second, parameter);
  });
}

function splitSegmentsAtIntersections(segments) {
  for (let first = 0; first < segments.length; first += 1) {
    for (let second = first + 1; second < segments.length; second += 1) {
      splitAtPairIntersections(segments[first], segments[second]);
    }
  }
}

function createNodeRegistry() {
  const nodes = [];
  const cells = new Map();
  const cellKey = (x, z) => `${x},${z}`;
  const cellCoordinate = value => Math.floor(value / JOIN_TOLERANCE_MM);
  const nodeForPoint = rawPoint => {
    const value = point(rawPoint);
    const cellX = cellCoordinate(value.x);
    const cellZ = cellCoordinate(value.z);
    for (let dx = -1; dx <= 1; dx += 1) {
      for (let dz = -1; dz <= 1; dz += 1) {
        for (const nodeId of cells.get(cellKey(cellX + dx, cellZ + dz)) || []) {
          if (distance(nodes[nodeId].point, value) <= JOIN_TOLERANCE_MM) return nodes[nodeId];
        }
      }
    }
    const node = { id: nodes.length, point: value, adjacency: new Map() };
    nodes.push(node);
    const key = cellKey(cellX, cellZ);
    if (!cells.has(key)) cells.set(key, []);
    cells.get(key).push(node.id);
    return node;
  };
  return { nodes, nodeForPoint };
}

function buildPlanarGraph(segments) {
  extendBoundarySegmentsToNearbyIntersections(segments);
  splitSegmentsAtIntersections(segments);
  const registry = createNodeRegistry();
  const edges = new Map();
  segments.forEach(segment => {
    segment.parameters.sort((left, right) => left - right);
    for (let index = 0; index + 1 < segment.parameters.length; index += 1) {
      const a = pointAt(segment, segment.parameters[index]);
      const b = pointAt(segment, segment.parameters[index + 1]);
      if (distance(a, b) < MIN_SEGMENT_LENGTH_MM) continue;
      const nodeA = registry.nodeForPoint(a);
      const nodeB = registry.nodeForPoint(b);
      if (nodeA.id === nodeB.id) continue;
      const key = nodeA.id < nodeB.id ? `${nodeA.id}|${nodeB.id}` : `${nodeB.id}|${nodeA.id}`;
      let edge = edges.get(key);
      if (!edge) {
        edge = { key, a: nodeA.id, b: nodeB.id, sources: [] };
        edges.set(key, edge);
      }
      segment.sources.forEach(source => {
        const sourceKey = refKey(source.ref);
        if (!edge.sources.some(existing => refKey(existing.ref) === sourceKey)) edge.sources.push(source);
      });
    }
  });
  edges.forEach(edge => {
    registry.nodes[edge.a].adjacency.set(edge.b, edge);
    registry.nodes[edge.b].adjacency.set(edge.a, edge);
  });
  return { nodes: registry.nodes, edges };
}

function orderedNeighbours(node, nodes) {
  return [...node.adjacency.keys()].sort((left, right) => {
    const leftPoint = nodes[left].point;
    const rightPoint = nodes[right].point;
    return Math.atan2(leftPoint.z - node.point.z, leftPoint.x - node.point.x)
      - Math.atan2(rightPoint.z - node.point.z, rightPoint.x - node.point.x);
  });
}

function traceFaces(graph) {
  const neighbourOrder = graph.nodes.map(node => orderedNeighbours(node, graph.nodes));
  const visited = new Set();
  const faces = [];
  graph.edges.forEach(edge => {
    [[edge.a, edge.b], [edge.b, edge.a]].forEach(([startA, startB]) => {
      const startKey = `${startA}>${startB}`;
      if (visited.has(startKey)) return;
      const nodeIds = [];
      let from = startA;
      let to = startB;
      let closed = false;
      const guardLimit = graph.edges.size * 2 + 4;
      for (let guard = 0; guard < guardLimit; guard += 1) {
        const directedKey = `${from}>${to}`;
        if (visited.has(directedKey) && directedKey !== startKey) break;
        visited.add(directedKey);
        nodeIds.push(from);
        const neighbours = neighbourOrder[to];
        const reverseIndex = neighbours.indexOf(from);
        if (reverseIndex < 0 || neighbours.length < 2) break;
        const next = neighbours[(reverseIndex - 1 + neighbours.length) % neighbours.length];
        from = to;
        to = next;
        if (from === startA && to === startB) {
          closed = true;
          break;
        }
      }
      if (!closed || nodeIds.length < 3 || new Set(nodeIds).size !== nodeIds.length) return;
      faces.push(nodeIds);
    });
  });
  return faces;
}

function uniqueEdgeSources(edge) {
  const unique = [];
  (edge?.sources || []).forEach(source => {
    if (!source?.ref) return;
    const key = refKey(source.ref);
    if (key && !unique.some(entry => refKey(entry.ref) === key)) unique.push(source);
  });
  return unique;
}

function bestSourcePairAtCorner(previousEdge, nextEdge, cornerPoint) {
  let best = null;
  for (const previousSource of uniqueEdgeSources(previousEdge)) {
    for (const nextSource of uniqueEdgeSources(nextEdge)) {
      if (refKey(previousSource.ref) === refKey(nextSource.ref)) continue;
      const parameters = lineIntersectionParameters(previousSource, nextSource);
      if (!parameters) continue;
      const intersection = pointAt(previousSource, parameters.first);
      const cornerDistance = distance(intersection, cornerPoint);
      if (cornerDistance > MAX_BOUNDARY_EXTENSION_MM + JOIN_TOLERANCE_MM) continue;
      const sourceLength = distance(previousSource.a, previousSource.b)
        + distance(nextSource.a, nextSource.b);
      const materiallyCloser = best && cornerDistance < best.cornerDistance - JOIN_TOLERANCE_MM;
      const similarlyClose = best && cornerDistance <= best.cornerDistance + JOIN_TOLERANCE_MM;
      if (!best || materiallyCloser || (similarlyClose && sourceLength > best.sourceLength)) {
        best = { previousSource, nextSource, cornerDistance, sourceLength };
      }
    }
  }
  return best;
}

function closestSourceAtCorner(previousEdge, nextEdge, cornerPoint) {
  const sources = [...uniqueEdgeSources(previousEdge), ...uniqueEdgeSources(nextEdge)];
  return sources.sort((left, right) => (
    distancePointToSegment(cornerPoint, left.a, left.b)
    - distancePointToSegment(cornerPoint, right.a, right.b)
  ))[0] || null;
}

function vertexForFaceCorner(nodeId, previousEdge, nextEdge, graph) {
  const node = graph.nodes[nodeId];
  const sourcePair = bestSourcePairAtCorner(previousEdge, nextEdge, node.point);
  if (sourcePair) {
    return {
      point: point(node.point),
      ref: {
        type: 'area-edge-intersection',
        edgeA: sourcePair.previousSource.ref,
        edgeB: sourcePair.nextSource.ref,
        fallbackPoint: point(node.point),
        role: 'corner'
      }
    };
  }
  const source = closestSourceAtCorner(previousEdge, nextEdge, node.point);
  if (source) {
    return {
      point: point(node.point),
      ref: { ...source.ref, t: clamp01(parameterOnSegment(node.point, source)) }
    };
  }
  return {
    point: point(node.point),
    ref: { type: 'free-point', fallbackPoint: point(node.point), role: 'free' }
  };
}

function simplifyFaceEntries(nodeIds, graph) {
  const entries = nodeIds.map((nodeId, originalIndex) => ({ nodeId, originalIndex }));
  let changed = true;
  while (changed && entries.length > 3) {
    changed = false;
    for (let index = 0; index < entries.length; index += 1) {
      const previous = graph.nodes[entries[(index - 1 + entries.length) % entries.length].nodeId].point;
      const current = graph.nodes[entries[index].nodeId].point;
      const next = graph.nodes[entries[(index + 1) % entries.length].nodeId].point;
      const incoming = sub(current, previous);
      const outgoing = sub(next, current);
      const scale = Math.max(1, distance(previous, current) * distance(current, next));
      const span = sub(next, previous);
      const spanLength = distance(previous, next);
      const lineDeviation = spanLength > Number.EPSILON
        ? Math.abs(cross(span, sub(current, previous))) / spanLength
        : Number.POSITIVE_INFINITY;
      const hasDominantContinuation = Math.max(
        distance(previous, current),
        distance(current, next)
      ) >= DOMINANT_EDGE_MIN_LENGTH_MM;
      const exactlyCollinear = Math.abs(cross(incoming, outgoing)) <= scale * 1e-9;
      const followsDominantLine = hasDominantContinuation && lineDeviation <= JOIN_TOLERANCE_MM;
      if ((!exactlyCollinear && !followsDominantLine) || dot(incoming, outgoing) < 0) continue;
      entries.splice(index, 1);
      changed = true;
      break;
    }
  }
  return entries;
}

function boundarySourcesBetween(nodeIds, graph, startIndex, endIndex, step) {
  const sources = [];
  let currentIndex = startIndex;
  for (let guard = 0; guard <= nodeIds.length && currentIndex !== endIndex; guard += 1) {
    const nextIndex = (currentIndex + step + nodeIds.length) % nodeIds.length;
    const edge = graph.nodes[nodeIds[currentIndex]].adjacency.get(nodeIds[nextIndex]);
    uniqueEdgeSources(edge).forEach(source => {
      const key = refKey(source.ref);
      if (key && !sources.some(existing => refKey(existing.ref) === key)) sources.push(source);
    });
    currentIndex = nextIndex;
  }
  return { sources };
}

function createAutomaticAreaDetector(rawSegments = [], clickPoint = null) {
  const segments = prepareSegments(rawSegments, clickPoint ? point(clickPoint) : null);
  if (segments.length < 3) return { detect: () => null, faceCount: 0 };
  const graph = buildPlanarGraph(segments);
  const faces = traceFaces(graph);
  const matches = [];
  faces.forEach(nodeIds => {
    const entries = simplifyFaceEntries(nodeIds, graph);
    const points = entries.map(entry => point(graph.nodes[entry.nodeId].point));
    const signedArea = polygonSignedAreaSquareMillimeters(points);
    const area = Math.abs(signedArea);
    if (area < MIN_FACE_AREA_SQUARE_MM || polygonHasSelfIntersections(points)) return;
    const vertices = entries.map((entry, index) => {
      const previousEntry = entries[(index - 1 + entries.length) % entries.length];
      const nextEntry = entries[(index + 1) % entries.length];
      return vertexForFaceCorner(
        entry.nodeId,
        boundarySourcesBetween(nodeIds, graph, entry.originalIndex, previousEntry.originalIndex, -1),
        boundarySourcesBetween(nodeIds, graph, entry.originalIndex, nextEntry.originalIndex, 1),
        graph
      );
    });
    matches.push({ vertices, points, areaSquareMeters: area / 1_000_000 });
  });
  matches.sort((left, right) => left.areaSquareMeters - right.areaSquareMeters);
  return {
    faceCount: matches.length,
    detect(candidatePoint) {
      const click = point(candidatePoint);
      return matches.find(match => pointInPolygon(click, match.points)) || null;
    }
  };
}

export function detectAutomaticAreaPolygon(clickPoint, rawSegments = []) {
  return createAutomaticAreaDetector(rawSegments, clickPoint).detect(clickPoint);
}

function boundsForSegment(candidate) {
  const a = point(candidate?.a);
  const b = point(candidate?.b);
  return {
    minX: Math.min(a.x, b.x),
    minZ: Math.min(a.z, b.z),
    maxX: Math.max(a.x, b.x),
    maxZ: Math.max(a.z, b.z)
  };
}

function mergeBounds(first, second) {
  if (!first) return second ? { ...second } : null;
  if (!second) return { ...first };
  return {
    minX: Math.min(first.minX, second.minX),
    minZ: Math.min(first.minZ, second.minZ),
    maxX: Math.max(first.maxX, second.maxX),
    maxZ: Math.max(first.maxZ, second.maxZ)
  };
}

function boundsIntersect(first, second, margin = 0) {
  if (!first || !second) return false;
  return first.maxX + margin >= second.minX
    && first.minX - margin <= second.maxX
    && first.maxZ + margin >= second.minZ
    && first.minZ - margin <= second.maxZ;
}

function sourceKeysForRef(rawRef = {}) {
  const ref = normalizeGeometryRef(rawRef);
  if (!ref) return [];
  const system = String(ref.systemType || ref.systemId || '*').trim() || '*';
  return [...new Set([
    ref.entityId,
    ref.sourceId,
    ref.groupId
  ].map(value => String(value || '').trim()).filter(Boolean).map(id => `${system}:${id}`))];
}

function sourceKeysForDetectedArea(detected) {
  return [...new Set((detected?.vertices || []).flatMap(vertex => {
    if (vertex?.ref?.type === 'area-edge-intersection') {
      return [
        ...sourceKeysForRef(vertex.ref.edgeA),
        ...sourceKeysForRef(vertex.ref.edgeB)
      ];
    }
    return sourceKeysForRef(vertex?.ref);
  }))];
}

function spatialCellKey(x, z) {
  return `${x}:${z}`;
}

function createAutomaticAreaSpatialIndex(candidates = []) {
  const cells = new Map();
  const overflow = [];
  const segments = candidates.filter(candidate => !candidate?.kind || candidate.kind === 'segment');
  const addToCell = (key, candidate) => {
    const entries = cells.get(key) || [];
    entries.push(candidate);
    cells.set(key, entries);
  };
  const candidateIds = new Map();
  segments.forEach((candidate, index) => candidateIds.set(candidate, index));
  for (const candidate of segments) {
    const bounds = boundsForSegment(candidate);
    const x0 = Math.floor(bounds.minX / AUTOMATIC_AREA_SPATIAL_CELL_MM);
    const x1 = Math.floor(bounds.maxX / AUTOMATIC_AREA_SPATIAL_CELL_MM);
    const z0 = Math.floor(bounds.minZ / AUTOMATIC_AREA_SPATIAL_CELL_MM);
    const z1 = Math.floor(bounds.maxZ / AUTOMATIC_AREA_SPATIAL_CELL_MM);
    if ((x1 - x0 + 1) * (z1 - z0 + 1) > 64) {
      overflow.push(candidate);
      continue;
    }
    for (let x = x0; x <= x1; x += 1) {
      for (let z = z0; z <= z1; z += 1) addToCell(spatialCellKey(x, z), candidate);
    }
  }
  return { candidates: segments, candidateIds, cells, overflow, detectorCache: new Map() };
}

function automaticAreaClickGeometryMatches(project, revision) {
  return Boolean(
    automaticAreaClickGeometryState
    && automaticAreaClickGeometryState.project === project
    && automaticAreaClickGeometryState.projectRevision === revision
    && automaticAreaClickGeometryState.dimensionRevision === getDimensionGeometryRevision()
  );
}

function prepareAutomaticAreaClickGeometry(project = S.project) {
  const projectRevision = getProjectModelRevision();
  if (automaticAreaClickGeometryMatches(project, projectRevision)) return automaticAreaClickGeometryState;
  const candidates = collectDimensionSnapCandidates(project, {
    includeRenderedSvg: false,
    preferDrawingObjects: true
  });
  automaticAreaClickGeometryState = {
    project,
    projectRevision,
    dimensionRevision: getDimensionGeometryRevision(),
    spatialIndex: createAutomaticAreaSpatialIndex(candidates)
  };
  return automaticAreaClickGeometryState;
}

function cancelAutomaticAreaClickGeometryPreparation() {
  const pending = automaticAreaClickPreparationHandle;
  automaticAreaClickPreparationHandle = null;
  if (!pending) return;
  globalThis.clearTimeout?.(pending.id);
}

function scheduleAutomaticAreaClickGeometryPreparation() {
  cancelAutomaticAreaClickGeometryPreparation();
  const project = S.project;
  const run = () => {
    automaticAreaClickPreparationHandle = null;
    if (!isAutoAreaMeasurementToolActive() || S.project !== project) return;
    prepareAutomaticAreaClickGeometry(project);
  };
  automaticAreaClickPreparationHandle = {
    id: globalThis.setTimeout?.(run, 0)
  };
}

function queryAutomaticAreaSpatialIndex(index, bounds) {
  if (!index || !bounds) return [];
  const x0 = Math.floor(bounds.minX / AUTOMATIC_AREA_SPATIAL_CELL_MM);
  const x1 = Math.floor(bounds.maxX / AUTOMATIC_AREA_SPATIAL_CELL_MM);
  const z0 = Math.floor(bounds.minZ / AUTOMATIC_AREA_SPATIAL_CELL_MM);
  const z1 = Math.floor(bounds.maxZ / AUTOMATIC_AREA_SPATIAL_CELL_MM);
  const result = new Set(index.overflow);
  for (let x = x0; x <= x1; x += 1) {
    for (let z = z0; z <= z1; z += 1) {
      for (const candidate of index.cells.get(spatialCellKey(x, z)) || []) result.add(candidate);
    }
  }
  return [...result];
}

function detectAutomaticAreaPolygonLocally(seed, spatialIndex, boundsHint = null) {
  const hintSpan = boundsHint
    ? Math.max(boundsHint.maxX - boundsHint.minX, boundsHint.maxZ - boundsHint.minZ)
    : 0;
  let radius = Math.max(AUTOMATIC_AREA_QUERY_MIN_RADIUS_MM, hintSpan * 1.25);
  for (let attempt = 0; attempt < AUTOMATIC_AREA_QUERY_ATTEMPTS; attempt += 1) {
    const local = queryAutomaticAreaSpatialIndex(spatialIndex, {
      minX: seed.x - radius,
      minZ: seed.z - radius,
      maxX: seed.x + radius,
      maxZ: seed.z + radius
    });
    const detectorKey = local
      .map(candidate => spatialIndex.candidateIds.get(candidate))
      .filter(Number.isInteger)
      .sort((left, right) => left - right)
      .join(',');
    let detector = spatialIndex.detectorCache.get(detectorKey);
    if (!detector) {
      detector = createAutomaticAreaDetector(local, seed);
      spatialIndex.detectorCache.set(detectorKey, detector);
    }
    const detected = detector.detect(seed);
    if (detected) return detected;
    radius *= 2;
  }
  return detectAutomaticAreaPolygon(seed, spatialIndex?.candidates || []);
}

function freeVertexAt(candidate) {
  const fallbackPoint = point(candidate);
  return {
    point: fallbackPoint,
    ref: { type: 'free-point', fallbackPoint, role: 'free' }
  };
}

function validResolvedPolygon(points = []) {
  return points.length >= 3
    && !polygonHasSelfIntersections(points)
    && Math.abs(polygonSignedAreaSquareMillimeters(points)) >= MIN_FACE_AREA_SQUARE_MM;
}

// Die Graphkontur ist die geprüfte geometrische Wahrheit. Persistente
// Kantenreferenzen werden nur beibehalten, wenn sie beim erneuten Auflösen
// wieder exakt zu diesem Graphknoten führen. Bei überlagerten SVG-/Asset-
// Konturen kann dieselbe Indexreferenz sonst eine andere Teilkante treffen
// und aus einer gültigen Fläche nachträglich ein sich kreuzendes Polygon
// machen. Nur solche mehrdeutigen Ecken werden als stabiler Freipunkt abgelegt.
export function automaticAreaVerticesForPersistence(detected, project = S.project) {
  const targetPoints = Array.isArray(detected?.points) ? detected.points.map(point) : [];
  const measurement = normalizeAreaMeasurementEntity({ vertices: detected?.vertices || [] });
  if (measurement.vertices.length !== targetPoints.length || targetPoints.length < 3) {
    return targetPoints.map(freeVertexAt);
  }
  const resolvedPoints = resolveAreaMeasurementPoints(measurement, project);
  const repairedVertices = measurement.vertices.map((vertex, index) => (
    distance(resolvedPoints[index], targetPoints[index]) <= JOIN_TOLERANCE_MM
      ? vertex
      : freeVertexAt(targetPoints[index])
  ));
  const repairedPoints = resolveAreaMeasurementPoints({ ...measurement, vertices: repairedVertices }, project);
  return validResolvedPolygon(repairedPoints)
    ? repairedVertices
    : targetPoints.map(freeVertexAt);
}

function automaticAreaGeometrySignature(candidates = []) {
  return candidates
    .filter(candidate => !candidate?.kind || candidate.kind === 'segment')
    .map(candidate => {
      const ref = normalizeGeometryRef(candidate?.ref);
      const a = point(candidate?.a);
      const b = point(candidate?.b);
      return [
        ref?.systemType || ref?.systemId || '',
        ref?.sourceId || ref?.groupId || ref?.entityId || '',
        String(candidate?.geometrySource || ''),
        Math.round(a.x * 10),
        Math.round(a.z * 10),
        Math.round(b.x * 10),
        Math.round(b.z * 10)
      ].join(':');
    })
    .sort()
    .join('|');
}

function automaticCandidateIdentity(candidate, index) {
  const ref = normalizeGeometryRef(candidate?.ref) || {};
  return [
    ref.systemType || ref.systemId || '',
    ref.entityId || ref.sourceId || ref.groupId || '',
    ref.type || 'segment',
    ref.index ?? ref.corner ?? index,
    candidate?.geometrySource || ''
  ].join('|');
}

function automaticCandidateCoordinateSignature(candidate) {
  const a = point(candidate?.a);
  const b = point(candidate?.b);
  return `${Math.round(a.x * 10)}:${Math.round(a.z * 10)}:${Math.round(b.x * 10)}:${Math.round(b.z * 10)}`;
}

function createAutomaticCandidateSnapshot(candidates = []) {
  const entries = new Map();
  candidates.forEach((candidate, index) => {
    if (candidate?.kind && candidate.kind !== 'segment') return;
    const baseKey = automaticCandidateIdentity(candidate, index);
    let key = baseKey;
    let duplicate = 1;
    while (entries.has(key)) {
      key = `${baseKey}#${duplicate}`;
      duplicate += 1;
    }
    entries.set(key, {
      signature: automaticCandidateCoordinateSignature(candidate),
      bounds: boundsForSegment(candidate),
      sourceKeys: sourceKeysForRef(candidate?.ref)
    });
  });
  return entries;
}

function changedAutomaticCandidateGeometry(previous, current) {
  const sourceKeys = new Set();
  let bounds = null;
  const keys = new Set([...(previous?.keys?.() || []), ...(current?.keys?.() || [])]);
  for (const key of keys) {
    const before = previous?.get(key);
    const after = current?.get(key);
    if (before?.signature === after?.signature) continue;
    for (const sourceKey of before?.sourceKeys || []) sourceKeys.add(sourceKey);
    for (const sourceKey of after?.sourceKeys || []) sourceKeys.add(sourceKey);
    bounds = mergeBounds(bounds, before?.bounds);
    bounds = mergeBounds(bounds, after?.bounds);
  }
  return { sourceKeys, bounds };
}

function sourceKeysOverlap(first = [], second = new Set()) {
  if (!first.length || !second.size) return false;
  for (const left of first) {
    const leftId = String(left).split(':').at(-1);
    for (const right of second) {
      if (left === right || (leftId && leftId === String(right).split(':').at(-1))) return true;
    }
  }
  return false;
}

function automaticMeasurementBounds(measurement, project) {
  return measurement?.automaticBounds
    || areaMeasurementBounds(resolveAreaMeasurementPoints(measurement, project));
}

function automaticMeasurementsAffectedByChange(measurements, change, project) {
  if (!change || (!change.sourceKeys.size && !change.bounds)) return [];
  return measurements.filter(measurement => (
    sourceKeysOverlap(areaMeasurementSourceKeys(measurement), change.sourceKeys)
    || boundsIntersect(automaticMeasurementBounds(measurement, project), change.bounds, MAX_BOUNDARY_EXTENSION_MM)
  ));
}

function uniqueAutomaticAreaSeeds(measurement, project) {
  const resolvedPoints = resolveAreaMeasurementPoints(measurement, project);
  const seeds = [
    point(measurement.automaticSeed),
    polygonInteriorLabelPoint(resolvedPoints)
  ];
  return seeds.filter((seed, index) => (
    Number.isFinite(seed.x)
    && Number.isFinite(seed.z)
    && !seeds.slice(0, index).some(previous => distance(previous, seed) <= JOIN_TOLERANCE_MM)
  ));
}

function automaticVerticesSignature(vertices = []) {
  return JSON.stringify(vertices.map(vertex => ({ point: point(vertex.point), ref: vertex.ref })));
}

// Die erste ausgelieferte Automatikversion speicherte noch kein Herkunftsflag.
// Ihre Eckpunkte sind jedoch fast vollständig rechnerische Kantenschnitte;
// frei von Hand gesetzte Punktpolygone besitzen diese Signatur nicht. So
// bleiben bereits angelegte Automatikflächen dynamisch, ohne gewöhnliche
// manuelle Flächen pauschal umzudeuten.
function isLegacyAutomaticAreaMeasurement(measurement) {
  const vertices = Array.isArray(measurement?.vertices) ? measurement.vertices : [];
  if (vertices.length < 3 || measurement?.automatic === false) return false;
  const intersectionCount = vertices.filter(vertex => vertex?.ref?.type === 'area-edge-intersection').length;
  return intersectionCount >= Math.max(2, vertices.length - 1)
    && vertices.every(vertex => (
      vertex?.ref?.type === 'area-edge-intersection'
      || vertex?.ref?.type === 'free-point'
    ));
}

// Automatisch erzeugte Flächen bleiben an den aktuellen Raumkantengraph
// gebunden. Eine reine Auflösung ihrer alten Eckreferenzen genügt nicht:
// Verschiebt sich eine Wand, können neue Schnittpunkte entstehen oder eine
// zuvor nicht angrenzende Kante zur Begrenzung werden. Darum wird bei jeder
// geänderten Kandidatengeometrie die komplette geschlossene Fläche erneut
// aus einem Innenpunkt erkannt. Manuell nachbearbeitete Flächen verlieren in
// area_measurement_service.js bewusst den automatic-Status.
export function refreshAutomaticAreaMeasurementsForCurrentGeometry(project = S.project, options = {}) {
  const allMeasurements = Array.isArray(project?.areaMeasurements)
    ? project.areaMeasurements.filter(measurement => (
        measurement?.automatic === true || isLegacyAutomaticAreaMeasurement(measurement)
      ))
    : [];
  const measurements = Array.isArray(options.measurements)
    ? options.measurements
    : allMeasurements;
  if (!allMeasurements.length) {
    if (lastAutomaticAreaProject === project) lastAutomaticAreaGeometrySignature = '';
    return false;
  }
  if (!measurements.length) return false;

  const candidates = Array.isArray(options.candidates)
    ? options.candidates
    : collectDimensionSnapCandidates(project, {
        includeRenderedSvg: false,
        preferDrawingObjects: true
      });
  if (options.force !== true) {
    const signature = automaticAreaGeometrySignature(candidates);
    if (lastAutomaticAreaProject === project
      && signature === lastAutomaticAreaGeometrySignature) return false;
    lastAutomaticAreaProject = project;
    lastAutomaticAreaGeometrySignature = signature;
  }

  let changed = false;
  const spatialIndex = options.spatialIndex || createAutomaticAreaSpatialIndex(candidates);
  for (const measurement of measurements) {
    if (measurement.automatic !== true) {
      measurement.automatic = true;
      measurement.automaticSeed = polygonInteriorLabelPoint(resolveAreaMeasurementPoints(measurement, project));
      changed = true;
    }
    let detected = null;
    for (const seed of uniqueAutomaticAreaSeeds(measurement, project)) {
      detected = detectAutomaticAreaPolygonLocally(
        seed,
        spatialIndex,
        automaticMeasurementBounds(measurement, project)
      );
      if (detected) break;
    }
    if (!detected) continue;
    const vertices = automaticAreaVerticesForPersistence(detected, project);
    if (vertices.length < 3) continue;
    const nextSourceKeys = sourceKeysForDetectedArea(detected);
    const nextBounds = areaMeasurementBounds(detected.points);
    const verticesChanged = automaticVerticesSignature(vertices) !== automaticVerticesSignature(measurement.vertices);
    const metadataChanged = JSON.stringify(nextSourceKeys) !== JSON.stringify(areaMeasurementSourceKeys(measurement))
      || JSON.stringify(nextBounds) !== JSON.stringify(measurement.automaticBounds || null)
      || measurement.automaticDetectionVersion !== AUTO_AREA_MEASUREMENT_DETECTION_VERSION;
    if (!verticesChanged && !metadataChanged) continue;
    if (verticesChanged) measurement.vertices = vertices;
    if (nextSourceKeys.length) measurement.automaticSourceKeys = nextSourceKeys;
    else delete measurement.automaticSourceKeys;
    if (nextBounds) measurement.automaticBounds = nextBounds;
    measurement.automaticDetectionVersion = AUTO_AREA_MEASUREMENT_DETECTION_VERSION;
    measurement.updatedAt = Date.now();
    changed = true;
  }
  if (changed && project === S.project && S.file) S.file.dirty = true;
  return changed;
}

// Wird vom App-Lebenszyklus nach einer verbindlichen Projektmutation aufgerufen,
// nicht vom Renderer. Der erste Lauf eines Projekts baut nur einen neutralen
// Geometrie-Basisstand auf. Spätere Läufe vergleichen stabile Segment-IDs und
// aktualisieren ausschließlich Räume, deren Quellen oder räumlicher Bereich
// tatsächlich betroffen sind. Während einer Drag-Vorschau bleibt die bisherige
// Fläche bewusst stehen; der Abschluss-Refresh übernimmt die neue Geometrie.
export function flushAutomaticAreaMeasurementsAfterProjectChange(project = S.project, options = {}) {
  if (!project || typeof project !== 'object' || S.drag?.active) return false;
  const revision = getProjectModelRevision();
  if (automaticAreaGeometryState?.project === project
    && automaticAreaGeometryState.revision === revision
    && options.force !== true) return false;

  const allAreaMeasurements = Array.isArray(project.areaMeasurements)
    ? project.areaMeasurements
    : [];
  const measurements = allAreaMeasurements.filter(measurement => (
        measurement?.automatic === true || isLegacyAutomaticAreaMeasurement(measurement)
      ));
  const staleMeasurements = measurements.filter(measurement => (
    measurement?.automaticDetectionVersion !== AUTO_AREA_MEASUREMENT_DETECTION_VERSION
  ));
  if (!allAreaMeasurements.length) {
    automaticAreaGeometryState = { project, revision, snapshot: null, candidates: null };
    return false;
  }

  const candidates = Array.isArray(options.candidates)
    ? options.candidates
    : collectDimensionSnapCandidates(project, {
        includeRenderedSvg: false,
        preferDrawingObjects: true,
        force: options.forceCandidates === true
      });
  const snapshot = createAutomaticCandidateSnapshot(candidates);
  const previous = automaticAreaGeometryState?.project === project
    ? automaticAreaGeometryState
    : null;
  automaticAreaGeometryState = { project, revision, snapshot, candidates };

  // Beim Laden/Undo ist ein Polygon der aktuellen Erkennung bereits der
  // richtige Projektzustand. Nach einer Änderung des Erkennungsalgorithmus
  // werden nur ältere Automatikflächen einmalig neu aufgebaut; dadurch werden
  // auch gespeicherte fehlerhafte Konturen repariert, ohne bei jedem Laden alle
  // Räume erneut zu analysieren.
  if (!previous?.snapshot && options.force !== true) {
    if (!staleMeasurements.length) return false;
    return refreshAutomaticAreaMeasurementsForCurrentGeometry(project, {
      candidates,
      measurements: staleMeasurements,
      spatialIndex: createAutomaticAreaSpatialIndex(candidates),
      force: true
    });
  }

  const change = changedAutomaticCandidateGeometry(previous?.snapshot, snapshot);
  invalidateAreaMeasurementDerivedCachesForGeometryChange(project, change);
  if (!measurements.length) return false;
  const affected = options.force === true
    ? measurements
    : [...new Set([
        ...automaticMeasurementsAffectedByChange(measurements, change, project),
        ...staleMeasurements
      ])];
  if (!affected.length) return false;
  return refreshAutomaticAreaMeasurementsForCurrentGeometry(project, {
    candidates,
    measurements: affected,
    spatialIndex: createAutomaticAreaSpatialIndex(candidates),
    force: true
  });
}

function setStatusText(text) {
  const element = document.getElementById('statusText') || document.getElementById('statusbar');
  if (element) element.textContent = text;
  if (!S.ui || typeof S.ui !== 'object') S.ui = {};
  S.ui.statusText = text;
}

function pointerWorld(event) {
  const svg = document.getElementById('planSvg');
  const rect = svg?.getBoundingClientRect?.();
  if (!rect) return null;
  const scale = numeric(S.camera2D?.scale, 1) || 1;
  return {
    x: (numeric(event.clientX) - rect.left - rect.width / 2) / scale + numeric(S.camera2D?.x),
    z: (numeric(event.clientY) - rect.top - rect.height / 2) / scale + numeric(S.camera2D?.z)
  };
}

export function isAutoAreaMeasurementToolActive() {
  return S.ui?.activeTool?.owner === 'core' && S.ui.activeTool.tool === AUTO_AREA_MEASUREMENT_TOOL;
}

export function syncAutoAreaMeasurementToolbarButton() {
  const button = document.getElementById('btnAutoAreaMeasurementTool');
  if (!button) return;
  const active = isAutoAreaMeasurementToolActive();
  button.classList.toggle('is-active', active);
  button.setAttribute('aria-pressed', active ? 'true' : 'false');
}

export function activateAutoAreaMeasurementTool() {
  if (!S.ui || typeof S.ui !== 'object') S.ui = {};
  S.ui.activeTool = { owner: 'core', tool: AUTO_AREA_MEASUREMENT_TOOL, meta: {} };
  setStatusText('Automatische Flächenmessung · in eine geschlossene Fläche klicken');
  syncAutoAreaMeasurementToolbarButton();
  scheduleAutomaticAreaClickGeometryPreparation();
  return true;
}

export function deactivateAutoAreaMeasurementTool() {
  automaticAreaClickRequestRevision += 1;
  cancelAutomaticAreaClickGeometryPreparation();
  if (isAutoAreaMeasurementToolActive()) S.ui.activeTool = null;
  syncAutoAreaMeasurementToolbarButton();
  setStatusText('Automatische Flächenmessung deaktiviert');
  AppRuntime.requestPlanRender('auto-area-measurement-tool-off');
  return true;
}

export function cancelAutoAreaMeasurementInteraction() {
  if (!isAutoAreaMeasurementToolActive()) return false;
  deactivateAutoAreaMeasurementTool();
  return true;
}

export function handleAutoAreaMeasurementPointerDown(event) {
  if (!isAutoAreaMeasurementToolActive()
    || S.project?.currentView !== 'plan'
    || (Number.isFinite(event.button) && event.button !== 0)) return false;
  if (event.target?.closest?.('[data-area-measurement-id], [data-area-measurement-vertex], [data-area-measurement-insert]')) {
    return false;
  }
  const world = pointerWorld(event);
  if (!world) return false;
  event.preventDefault?.();
  event.stopPropagation?.();
  const project = S.project;
  const requestRevision = automaticAreaClickRequestRevision + 1;
  automaticAreaClickRequestRevision = requestRevision;
  cancelAutomaticAreaClickGeometryPreparation();
  if (findAreaMeasurementContainingPoint(world, project)) {
    setStatusText('Fläche bereits bemasst · bitte eine unbemasste Fläche wählen');
    return true;
  }
  setStatusText('Automatische Flächenmessung · Begrenzung wird erkannt …');
  globalThis.setTimeout?.(() => {
    if (requestRevision !== automaticAreaClickRequestRevision
      || !isAutoAreaMeasurementToolActive()
      || S.project !== project) return;
    try {
      const detected = detectAutomaticAreaPolygonLocally(
        world,
        prepareAutomaticAreaClickGeometry(project).spatialIndex
      );
      if (!detected) {
        setStatusText('Keine geschlossene Fläche erkannt · angrenzende Kanten müssen miteinander verbunden sein');
        return;
      }
      createAreaMeasurementFromVertices(automaticAreaVerticesForPersistence(detected), {
        automatic: true,
        automaticSeed: world,
        automaticSourceKeys: sourceKeysForDetectedArea(detected),
        automaticBounds: areaMeasurementBounds(detected.points),
        automaticDetectionVersion: AUTO_AREA_MEASUREMENT_DETECTION_VERSION,
        undoLabel: 'Automatische Flächenmessung setzen',
        refreshReason: 'auto-area-measurement-create',
        successPrefix: 'Fläche automatisch erkannt'
      });
    } catch (error) {
      console.error('Automatische Flächenmessung fehlgeschlagen', error);
      setStatusText('Automatische Flächenmessung fehlgeschlagen · bitte erneut versuchen');
    }
  }, 0);
  return true;
}
